import { useState, useEffect } from 'react';
import { QrCode, Timer } from 'lucide-react';
import { azureClient } from '../lib/azure-public';
import type { QuizConfig, Question, QuizAnswer } from '@solace-quiz/shared';
import { saveQuizState, loadQuizState } from '../lib/localStorage';
import { calculateRawScore, calculateAiIq, getAiIqDisplay, getIntelligenceTier, getInterpretation } from '@solace-quiz/shared';
import QuestionView from './QuestionView';
import TierComplete from './TierComplete';
import UserInfoForm from './UserInfoForm';
import YesNoQuestions from './YesNoQuestions';
import QRCodeModal from './QRCodeModal';
import EmailVerification from './EmailVerification';

type QuizState =
  | { phase: 'user-info' }
  | { phase: 'email-verification' }
  | { phase: 'qualifying' }
  | { phase: 'quiz'; currentQuestion: number }
  | { phase: 'complete' };

interface QuizAppProps {
  quizId: string;
}

export default function QuizApp({ quizId }: QuizAppProps) {
  const [config, setConfig] = useState<QuizConfig | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [state, setState] = useState<QuizState>({ phase: 'user-info' });
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [showQRModal, setShowQRModal] = useState(false);

  const [answers, setAnswers] = useState<{ questionId: string; selectedIndex: number; isCorrect: boolean; timeTaken: number }[]>([]);
  const [qualifyingAnswers, setQualifyingAnswers] = useState<{ questionId: string; answer: boolean }[]>([]);
  const [totalTimeMs, setTotalTimeMs] = useState(0);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        setLoadError(null);
        const data = await azureClient.quiz.get(quizId);
        setConfig(data);

        const expiryDays = data.localStorageExpiryDays || 7;
        const savedState = loadQuizState(expiryDays);

        if (savedState && savedState.sessionId) {
          try {
            const session = await azureClient.sessions.getById(savedState.sessionId);

            if (session) {
              setState(savedState.state);
              setSessionId(savedState.sessionId);
              setEmail(savedState.email);
              setName(savedState.name || session.name || '');
              setQrCode(savedState.qrCode);
              setAnswers(savedState.answers || savedState.tier1Answers || []);
              setQualifyingAnswers(savedState.qualifyingAnswers || []);
              setTotalTimeMs(savedState.totalTimeMs || 0);
            }
          } catch (error) {
            console.error('Error loading saved session:', error);
          }
        }
      } catch (err) {
        console.error('Failed to load quiz config:', err);
        setLoadError(`Quiz "${quizId}" not found. Please check the URL and try again.`);
      }
    };

    initializeApp();
  }, [quizId]);

  useEffect(() => {
    saveQuizState({
      state,
      sessionId,
      email,
      name,
      qrCode,
      answers,
      qualifyingAnswers,
      totalTimeMs,
    });
  }, [state, sessionId, email, name, qrCode, answers, qualifyingAnswers, totalTimeMs]);

  const generateVerificationCode = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  const sendVerificationEmail = async (email: string, name: string, code: string) => {
    const result = await azureClient.email.sendVerification({
      email,
      name,
      verificationCode: code,
    });
    return result;
  };

  const handleUserInfoSubmit = async (userEmail: string, userName: string) => {
    const trimmedEmail = userEmail.trim().toLowerCase();
    setEmail(trimmedEmail);
    setName(userName);

    try {
      const existingSession = await azureClient.sessions.getByEmail(trimmedEmail, quizId);

      if (existingSession && existingSession.id) {
        setSessionId(existingSession.id);
        setQrCode(existingSession.id);

        const answersData = await azureClient.answers.getBySession(existingSession.id, {
          orderBy: 'answered_at',
          order: 'asc',
        });

        setAnswers((answersData || []).map(a => ({
          questionId: a.question_id,
          selectedIndex: 0,
          isCorrect: a.is_correct,
          timeTaken: a.time_taken_ms || 0,
        })));

        setTotalTimeMs(existingSession.time_ms || 0);

        if (existingSession.qualifying_questions) {
          setQualifyingAnswers(existingSession.qualifying_questions as any);
        }

        if (existingSession.completed_at) {
          setState({ phase: 'complete' });
        } else {
          const answerCount = (answersData || []).length;
          if (answerCount > 0 && config && answerCount < config.questions.length) {
            setState({ phase: 'quiz', currentQuestion: answerCount });
          } else if (answerCount === 0) {
            if (existingSession.qualifying_questions && (existingSession.qualifying_questions as any[]).length >= config.qualifyingQuestions.length) {
              setState({ phase: 'quiz', currentQuestion: 0 });
            } else {
              setState({ phase: 'qualifying' });
            }
          } else if (config && answerCount >= config.questions.length) {
            setState({ phase: 'complete' });
          }
        }
        } else {
        const data = await azureClient.sessions.create({
          quiz_id: quizId,
          email: trimmedEmail,
          name: userName,
        });

        setSessionId(data.id);
        setQrCode(data.id);
        setState({ phase: 'qualifying' });
      }
    } catch (error) {
      console.error('Error with session:', error);
    }
  };

  const handleQuizSubmit = async (selectedIndex: number, isCorrect: boolean, timeTaken: number) => {
    if (!config || !sessionId) return;

    const question = config.questions[state.phase === 'quiz' ? state.currentQuestion : 0];

    // Check if answer already exists for this question
    const existingAnswerIndex = answers.findIndex(a => a.questionId === question.id);

    if (existingAnswerIndex === -1) {
      setAnswers((prev) => [
        ...prev,
        { questionId: question.id, selectedIndex, isCorrect, timeTaken },
      ]);
    }

    const newTotalTime = totalTimeMs + timeTaken;
    setTotalTimeMs(newTotalTime);

    try {
      // Use upsert to handle duplicate submissions gracefully
      await azureClient.answers.create({
        session_id: sessionId,
        question_id: question.id,
        tier: 1,
        selected_answer: question.options[selectedIndex],
        is_correct: isCorrect,
        time_taken_ms: timeTaken,
      });

      await azureClient.sessions.update(sessionId, { time_ms: newTotalTime });
    } catch (error) {
      console.error('Error saving answer:', error);
    }
  };

  const handleQuizAnswer = async (selectedIndex: number, isCorrect: boolean, timeTaken: number) => {
    if (!config || !sessionId) return;

    if (state.phase === 'quiz' && state.currentQuestion < config.questions.length - 1) {
      setState({ phase: 'quiz', currentQuestion: state.currentQuestion + 1 });
    } else {
      try {
        await azureClient.sessions.update(sessionId, {
          completed_at: new Date().toISOString(),
        });
      } catch (error) {
        console.error('Error marking session complete:', error);
      }

      setState({ phase: 'complete' });
    }
  };

  const handleVerifyCode = async (code: string): Promise<{ success: boolean; error?: string }> => {
    if (!sessionId) {
      return { success: false, error: 'Session not found' };
    }

    try {
      const session = await azureClient.sessions.getById(sessionId);

      if (!session) {
        return { success: false, error: 'Session not found' };
      }

      if (!session.verification_code || !session.verification_code_expires_at) {
        return { success: false, error: 'Verification code not found' };
      }

      const now = new Date();
      const expiresAt = new Date(session.verification_code_expires_at);

      if (now > expiresAt) {
        return { success: false, error: 'Verification code has expired' };
      }

      if (session.verification_code !== code) {
        return { success: false, error: 'Invalid verification code' };
      }

      await azureClient.sessions.update(sessionId, {
        email_verified_at: new Date().toISOString(),
      });

      setState({ phase: 'qualifying' });
      return { success: true };
    } catch (error) {
      console.error('Error verifying code:', error);
      return { success: false, error: 'Verification failed' };
    }
  };

  const handleResendCode = async () => {
    if (!sessionId) return;

    const verificationCode = generateVerificationCode();
    const expiresAt = new Date(Date.now() + 10 * 60 * 1000).toISOString();

    try {
      await azureClient.sessions.update(sessionId, {
        verification_code: verificationCode,
        verification_code_expires_at: expiresAt,
      });

      await sendVerificationEmail(email, name, verificationCode);
    } catch (error) {
      console.error('Error resending code:', error);
    }
  };

  const handleEmailVerified = () => {
    setState({ phase: 'qualifying' });
  };

  const handleQualifyingSubmit = async (newAnswers: { questionId: string; answer: boolean }[]) => {
    if (!sessionId) return;

    const allAnswers = [...qualifyingAnswers, ...newAnswers];
    setQualifyingAnswers(allAnswers);

    try {
      await azureClient.sessions.update(sessionId, {
        qualifying_questions: allAnswers as any,
      });
    } catch (error) {
      console.error('Error updating qualifying questions:', error);
    }

    setState({ phase: 'quiz', currentQuestion: 0 });
  };

  if (loadError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Quiz Not Found</h1>
          <p className="text-gray-600 mb-6">{loadError}</p>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  const showQRButton = state.phase !== 'user-info' && state.phase !== 'email-verification' && state.phase !== 'qualifying';
  const showTimer = state.phase === 'quiz';

  const formatTime = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    const milliseconds = Math.floor((ms % 1000) / 10);
    return `${mins}:${secs.toString().padStart(2, '0')}.${milliseconds.toString().padStart(2, '0')}`;
  };

  const getAiIqScoring = (quizAnswers: { questionId: string; selectedIndex: number; isCorrect: boolean; timeTaken: number }[], questions: Question[]) => {
    const answerData: QuizAnswer[] = quizAnswers.map(a => ({
      id: '',
      session_id: sessionId || '',
      question_id: a.questionId,
      tier: 1,
      selected_answer: a.selectedIndex.toString(),
      is_correct: a.isCorrect,
      time_taken_ms: a.timeTaken,
      answered_at: new Date().toISOString(),
    }));

    const rawScore = calculateRawScore(answerData, questions, config.timeMax);
    const aiIq = calculateAiIq(rawScore, questions.length);

    return {
      aiIqScore: getAiIqDisplay(aiIq),
      intelligenceTier: getIntelligenceTier(aiIq, config.scoring.tiers),
      interpretation: getInterpretation(aiIq, config.scoring.tiers),
      rawScore,
      aiIq,
    };
  };

  return (
    <>
      {showTimer && (
        <div className="fixed top-4 left-4 bg-white px-4 py-2 rounded-lg shadow-lg z-40 flex items-center gap-2">
          <Timer className="w-5 h-5 text-blue-600" />
          <div>
            <div className="text-xs text-gray-500 font-medium">Total Time</div>
            <div className="text-lg font-mono font-bold text-gray-900">{formatTime(totalTimeMs)}</div>
          </div>
        </div>
      )}

      {showQRButton && qrCode && (
        <button
          onClick={() => setShowQRModal(true)}
          className="fixed top-4 right-4 bg-brand-green text-brand-dark p-3 rounded-full shadow-lg hover:opacity-90 transition transform active:scale-95 z-40"
        >
          <QrCode className="w-6 h-6" />
        </button>
      )}

      {state.phase === 'user-info' && (
        <UserInfoForm
          quizId={quizId}
          onSubmit={handleUserInfoSubmit}
          messages={config.messages.userInfo}
        />
      )}

      {state.phase === 'email-verification' && (
        <EmailVerification
          email={email}
          name={name}
          onVerified={handleEmailVerified}
          onResendCode={handleResendCode}
          onVerifyCode={handleVerifyCode}
        />
      )}

      {state.phase === 'qualifying' && (
        <YesNoQuestions
          questions={config.qualifyingQuestions}
          onSubmit={handleQualifyingSubmit}
          messages={config.messages.qualifying}
        />
      )}

      {state.phase === 'quiz' && (
        <QuestionView
          question={config.questions[state.currentQuestion]}
          questionNumber={state.currentQuestion + 1}
          totalQuestions={config.questions.length}
          onAnswer={handleQuizAnswer}
          onSubmit={handleQuizSubmit}
          previousAnswer={answers[state.currentQuestion]}
          messages={config.messages.question}
        />
      )}

      {state.phase === 'complete' && (() => {
        const totalCorrect = answers.filter((a) => a.isCorrect).length;
        const scoring = getAiIqScoring(answers, config.questions);
        return (
          <TierComplete
            tier={1}
            score={totalCorrect}
            totalQuestions={config.questions.length}
            prizeMessage={config.prize}
            qrCode={qrCode}
            showContinue={false}
            aiIqScore={scoring.aiIqScore}
            intelligenceTier={scoring.intelligenceTier}
            interpretation={scoring.interpretation}
            messages={config.messages.quizComplete}
            socialShare={config.messages.socialShare}
            quizId={quizId}
          />
        );
      })()}

      {showQRModal && (
        <QRCodeModal qrCode={qrCode} onClose={() => setShowQRModal(false)} />
      )}
    </>
  );
}
